import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Paper, Chip, IconButton,
  CircularProgress, Pagination, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button, List, ListItem, ListItemText, Tooltip,
  Drawer, Stack,
} from '@mui/material'
import { Visibility, Delete, CompareArrows } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { analysisApi } from '../services/api'
import IdentitySourceBadge from '../components/IdentitySourceBadge'

const PAGE_SIZE = 15
// §22 — when sorted by cost, fetch this many (server cap is le=100) and
// sort/paginate client-side. cost_summary is computed from a JSON column
// server-side, not a SQL-sortable column, so there's no portable ORDER BY
// across Postgres and the SQLite this feature's own tests run against.
// Correct for any tenant with <=100 total scans; a known v1 approximation
// beyond that (sorts the 100 most recent, not a true global top-N).
const COST_SORT_FETCH_LIMIT = 100

const fmtCost = (v: number) => (v < 0.01 ? v.toFixed(4) : v.toFixed(2))

const AnalysesPage = () => {
  const [analyses, setAnalyses] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null)

  // Which files a scan did not analyse, opened from the coverage chip.
  //
  // The list response carries the counters and not the per-file rows — one
  // row per file across twenty-five scans would be thousands of rows to
  // render one column — so the rows are fetched from the analysis when
  // somebody actually asks for them.
  const [coverageFor, setCoverageFor] = useState<any | null>(null)
  const [coverageFiles, setCoverageFiles] = useState<any[] | null>(null)
  const [coverageChecks, setCoverageChecks] = useState<any[] | null>(null)
  const [coverageLoading, setCoverageLoading] = useState(false)

  const openCoverage = async (a: any) => {
    setCoverageFor(a)
    setCoverageFiles(null)
    setCoverageChecks(null)
    setCoverageLoading(true)
    try {
      const res = await analysisApi.getAnalysis(a.analysis_id)
      setCoverageFiles(res.data?.scan_coverage?.files || [])
      setCoverageChecks(res.data?.analyzer_coverage?.analyzers || [])
    } catch {
      setCoverageFiles([])
      setCoverageChecks([])
    } finally {
      setCoverageLoading(false)
    }
  }
  const [deleting, setDeleting] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [sortBy, setSortBy] = useState<'created_at' | 'cost'>(
    searchParams.get('sortBy') === 'cost' ? 'cost' : 'created_at',
  )

  useEffect(() => { fetchAnalyses() }, [page, sortBy])

  const fetchAnalyses = async () => {
    setLoading(true)
    try {
      const res = sortBy === 'cost'
        ? await analysisApi.getAnalyses({ limit: COST_SORT_FETCH_LIMIT, offset: 0 })
        : await analysisApi.getAnalyses({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE })
      const data = res.data
      setAnalyses(data.analyses || data || [])
      setTotal(data.total || 0)
    } catch (e) {
      console.error('Failed to fetch analyses:', e)
    } finally {
      setLoading(false)
    }
  }

  // §22 — when sorted by cost, `analyses` holds up to COST_SORT_FETCH_LIMIT
  // rows fetched once; sort + paginate that batch client-side rather than
  // re-fetching per page (the server already did the tenant-scoped fetch).
  const sortedByCost = sortBy === 'cost'
    ? [...analyses].sort((a, b) =>
        (b.cost_summary?.cost_per_request_usd?.high ?? -1) - (a.cost_summary?.cost_per_request_usd?.high ?? -1))
    : analyses
  const displayedAnalyses = sortBy === 'cost'
    ? sortedByCost.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : analyses
  const effectiveTotal = sortBy === 'cost' ? sortedByCost.length : total

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await analysisApi.deleteAnalysis(deleteTarget.analysis_id)
      setDeleteTarget(null)
      fetchAnalyses()
    } catch (e) {
      console.error('Failed to delete analysis:', e)
    } finally {
      setDeleting(false)
    }
  }

  // Why a file was not analysed, in words rather than in the ledger's vocabulary.
// The keys come from scanner-client/coverage_ledger.py; an unlisted one falls
// through to its raw value rather than being hidden, because a reason nobody
// has phrased yet is still a reason.
// Why an optional check did not run, in the customer's words. Mirrors the
// same table on AnalysisDetailPage; states come from
// services/analysis/analyzer_ledger.py.
// What a clean result means, per check.
//
// "ran, nothing to report" states an absence where the finding is a positive:
// no agent holds more access than its role justifies is a result somebody
// should be able to read as one. Keyed on the feature id, with a generic
// fallback so a check added later still reads sensibly.
const CLEAN_RESULT: Record<string, string> = {
  permission_fit: 'no agent holds more access than its role needs',
  escalation_paths: 'no agent can reach access it does not hold',
  objective_conflicts: 'no agents work against each other',
}

const ANALYZER_STATE_LABELS: Record<string, string> = {
  disabled_by_config: 'not enabled',
  not_entitled: 'not included in your plan',
  no_input: 'nothing in this scan to assess',
  errored: 'failed to run',
}

const COVERAGE_REASONS: Record<string, string> = {
  no_framework_detected: 'contained no recognisable agent framework',
  no_agent_extracted: 'had a framework but no agent we could read',
  excluded_by_pattern: 'excluded by your scan configuration',
  over_size_limit: 'larger than the scan size limit',
  max_files_reached: 'beyond the per-scan file limit',
  read_failed: 'could not be read',
  analyzer_error: 'failed during analysis',
}

const scoreColor = (score: number) => {
    if (score >= 8) return 'success'
    if (score >= 5) return 'warning'
    return 'error'
  }

  const totalPages = Math.ceil(effectiveTotal / PAGE_SIZE)

  const toggleCostSort = () => {
    setPage(1)
    setSortBy((prev) => (prev === 'cost' ? 'created_at' : 'cost'))
  }

  if (loading && page === 1) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Analyses</Typography>
        {total > 0 && (
          <Typography variant="body2" color="text.secondary">
            {total} total
            {sortBy === 'cost' && total > COST_SORT_FETCH_LIMIT && (
              <> — sorted by cost across the {COST_SORT_FETCH_LIMIT} most recent scans</>
            )}
          </Typography>
        )}
      </Box>

      <Card>
        <CardContent>
          {displayedAnalyses.length === 0 && !loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">No analyses yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Submit findings or trigger an analysis to see results here
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Repository</TableCell>
                      <TableCell>Commit / Branch</TableCell>
                      {/* Only shown when at least one scan on this page reports
                          coverage. Scans from a scanner that predates the
                          ledger have none, and a column of dashes would read
                          as "we covered nothing" rather than "we did not
                          measure". Same reasoning as the cost column below. */}
                      {analyses.some((a: any) => a.scan_coverage?.coverage_pct != null) && (
                        <TableCell>Files analysed</TableCell>
                      )}
                      <TableCell>Overall Score</TableCell>
                      <TableCell>Agents</TableCell>
                      <TableCell>Findings</TableCell>
                      <TableCell>KB Matches</TableCell>
                      {/* §22 — only shown when the backend actually returned
                          cost_estimation_visible (below-tier tenants get
                          cost_summary: null on every row, and the column
                          would be all dashes — skip it entirely instead). */}
                      {analyses.some((a: any) => a.cost_summary) && (
                        <TableCell>
                          <TableSortLabel active={sortBy === 'cost'} direction="desc" onClick={toggleCostSort}>
                            Projected cost
                          </TableSortLabel>
                        </TableCell>
                      )}
                      <TableCell>Created</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedAnalyses.map((a: any) => (
                      <TableRow key={a.analysis_id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/analyses/${a.analysis_id}`)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {a.repository_name || 'unknown'}
                            </Typography>
                            <IdentitySourceBadge source={a.repo_identity_source} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {a.analysis_id?.substring(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {a.commit_sha ? (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {a.commit_sha.substring(0, 8)}
                              {a.branch_name && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  · {a.branch_name}
                                </Typography>
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        {analyses.some((x: any) => x.scan_coverage?.coverage_pct != null) && (
                          <TableCell>
                            {(() => {
                              const c = a.scan_coverage
                              if (!c || c.coverage_pct == null) {
                                return (
                                  <Tooltip title="This scan came from a scanner that does not report coverage.">
                                    <Typography variant="caption" color="text.disabled" sx={{ cursor: 'help' }}>—</Typography>
                                  </Tooltip>
                                )
                              }
                              const skipped = (c.files_in_scope ?? 0) - (c.files_analyzed ?? 0)
                              const reasons = Object.entries(c.by_outcome || {})
                                .filter(([k]) => k !== 'analyzed' && k !== 'analyzed_via_tool_reach')
                                .map(([k, n]) => `${n} ${COVERAGE_REASONS[k] || k}`)
                              return (
                                <Tooltip
                                  arrow
                                  title={skipped > 0
                                    ? `${c.files_analyzed} of ${c.files_in_scope} files analysed — click to see which ${skipped} were not.`
                                    : `All ${c.files_in_scope} files in scope were analysed.`}
                                >
                                  <Chip
                                    size="small"
                                    label={`${c.files_analyzed}/${c.files_in_scope}`}
                                    variant={c.coverage_pct >= 90 ? 'filled' : 'outlined'}
                                    color={c.coverage_pct >= 90 ? 'success' : 'default'}
                                    onClick={(e) => { e.stopPropagation(); openCoverage(a) }}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                </Tooltip>
                              )
                            })()}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={`${(a.risk_score_overall || 0).toFixed(1)} / 10`}
                            color={scoreColor(a.risk_score_overall || 0) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{a.agents_detected ?? 0}</TableCell>
                        <TableCell>{a.findings_count ?? a.findings?.length ?? '-'}</TableCell>
                        <TableCell>{a.kb_matches?.length || 0}</TableCell>
                        {analyses.some((x: any) => x.cost_summary) && (
                          <TableCell>
                            {a.cost_summary?.cost_per_request_usd ? (
                              <Tooltip title={`Sum of ${a.cost_summary.entry_point_agents_total ?? a.cost_summary.agents_priced} independently-invoked agent(s); ${a.cost_summary.non_entry_point_agents_excluded ?? 0} delegate/internal agent(s) excluded (already counted inside their caller's estimate).`}>
                                <Chip
                                  label={`$${fmtCost(a.cost_summary.cost_per_request_usd.low)}–$${fmtCost(a.cost_summary.cost_per_request_usd.high)}/req`}
                                  size="small" variant="outlined"
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>{new Date(a.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/analyses/${a.analysis_id}`) }}>
                            <Visibility />
                          </IconButton>
                          {/* SD2 — Compare to another scan of the same repo. ScanDiffPage
                              auto-picks the previous scan as baseline. 404s gracefully
                              when FEATURE_RELEASE_DIFF is off. */}
                          {a.repo_identity_hash && (
                            <IconButton
                              size="small"
                              title="Compare to another scan of this repo"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/diff/${encodeURIComponent(a.repo_identity_hash)}?b=${encodeURIComponent(a.analysis_id)}`)
                              }}
                            >
                              <CompareArrows fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" color="error" onClick={(e) => {
                            e.stopPropagation()
                            setDeleteTarget(a)
                          }}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Analysis</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Permanently delete the analysis for <strong>{deleteTarget?.repository_name || 'this repository'}</strong>?
            This cannot be undone.
          </DialogContentText>
          <DialogContentText variant="body2" sx={{ mb: 1 }}>The following will be deleted:</DialogContentText>
          <List dense disablePadding sx={{ pl: 1 }}>
            <ListItem disableGutters>
              <ListItemText
                primary={`${deleteTarget?.findings_count ?? 0} findings`}
                secondary="All detected issues and risk scores"
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary={`${deleteTarget?.agents_detected ?? 0} agent fingerprints`}
                secondary="Behavioral fingerprints and drift history for this scan"
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemText
                primary={`${deleteTarget?.kb_matches?.length ?? 0} KB match records`}
                secondary="Knowledge base match scores and recommendations"
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Which files a scan did not analyse.
          A drawer rather than a row expansion, matching the topology detail
          panel: the reader keeps the list of scans in view while reading one
          scan's detail, which is what makes coverage comparable rather than
          just visible. */}
      <Drawer anchor="right" open={!!coverageFor} onClose={() => setCoverageFor(null)}>
        <Box sx={{ width: 420, p: 2, height: '100%', overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Scan coverage</Typography>
            <IconButton size="small" onClick={() => setCoverageFor(null)}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {coverageFor?.repository_name}
          </Typography>

          {(() => {
            const c = coverageFor?.scan_coverage
            if (!c) return null
            return (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {c.files_analyzed} of {c.files_in_scope} file
                  {c.files_in_scope === 1 ? '' : 's'} analysed ({c.coverage_pct}%)
                </Typography>
              </Box>
            )
          })()}

          {/* Which optional checks ran. Same question as which files were
              read — what this scan actually examined — so it belongs here
              rather than as chips above the findings, where three checks
              looked singled out from twenty-odd analyzers. */}
          {!coverageLoading && coverageChecks && coverageChecks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Optional checks</Typography>
              <Stack spacing={0.25}>
                {coverageChecks.map((a: any) => {
                  // The server resolves the label from the feature id on the
                  // way out, so a rename reaches scans recorded before it.
                  // Falling back to the id keeps a row readable rather than
                  // rendering "undefined — 10 findings".
                  const label = a.label || a.feature
                  // How much of the estate this analyzer could actually see.
                  // `state: 'ran'` says it executed; it does NOT say it had
                  // anything to work with. Escalation analysis on an estate of
                  // markdown subagents ran, found nothing, and was rendered as
                  // "no agent can reach access it does not hold" — while most
                  // agents were in a format that cannot express a delegation
                  // target at all. A clean result is only a claim over what was
                  // assessed.
                  //
                  // Absent means the analyzer does not report subject coverage,
                  // NOT that it assessed none — only escalation reports it
                  // today, and treating absence as zero would caveat every
                  // other row for no reason.
                  const partial =
                    a.subjects_considered != null &&
                    a.subjects_assessed != null &&
                    a.subjects_assessed < a.subjects_considered
                  const clean = partial
                    ? `${CLEAN_RESULT[a.feature] || 'nothing found'} — among the ${a.subjects_assessed} of ${a.subjects_considered} agents it could assess`
                    : (CLEAN_RESULT[a.feature] || 'ran, nothing found')
                  return (
                    <Typography
                      key={a.feature}
                      variant="caption"
                      color={partial && !a.findings ? 'warning.main' : 'text.secondary'}
                    >
                      {a.state === 'ran'
                        ? (a.findings
                            ? `${label} — ${a.findings} finding${a.findings === 1 ? '' : 's'}`
                            : `${label} — ${clean}`)
                        : `${label} — ${ANALYZER_STATE_LABELS[a.state] || a.state}`}
                    </Typography>
                  )
                })}
              </Stack>
            </Box>
          )}

          {coverageLoading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}

          {!coverageLoading && coverageFiles && (() => {
            const skipped = coverageFiles.filter((f: any) =>
              f.outcome !== 'analyzed' && f.outcome !== 'analyzed_via_tool_reach')
            if (!skipped.length) {
              return (
                <Typography variant="body2" color="text.secondary">
                  Every file in scope was analysed.
                </Typography>
              )
            }
            const byReason: Record<string, string[]> = {}
            skipped.forEach((f: any) => {
              byReason[f.outcome] = [...(byReason[f.outcome] || []), f.path]
            })
            return (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Not analysed</Typography>
                {Object.entries(byReason).map(([outcome, paths]) => (
                  <Box key={outcome} sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {paths.length} {COVERAGE_REASONS[outcome] || outcome}
                    </Typography>
                    <Stack sx={{ mt: 0.5 }}>
                      {paths.map(pth => (
                        <Typography key={pth} variant="caption" color="text.secondary"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {pth}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                  Folder names are not sent to ARIAS, so these paths will not match
                  your repository. Search your repository for the file name to find
                  each one.
                </Typography>
              </>
            )
          })()}
        </Box>
      </Drawer>
    </Box>
  )
}

export default AnalysesPage
